import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useKeycloak } from "../providers/keycloak-provider";
import AfdResults, { AfdData } from "../components/AfdResults";
import DashboardLayout, { LayoutProps } from "../components/DashboardLayout";

interface Props extends LayoutProps {
  dadosPath: string;
}

export default function PontosPage({ dadosPath, flash }: Props) {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [data,    setData]    = useState<AfdData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(10);
  const now = new Date();
  const defaultMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [selectedMonthYear, setSelectedMonthYear] = useState(defaultMonthYear);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const monthParam = selectedMonthYear ? `&month_year=${selectedMonthYear}` : "";
    axios.get<AfdData>(`${dadosPath}?page=${page}&per_page=${perPage}${monthParam}`, { headers: { Accept: "application/json", Authorization: `Bearer ${keycloak.token ?? ""}` } })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError("Erro ao carregar os registros. Tente novamente."); setLoading(false); });
  }, [dadosPath, page, perPage, selectedMonthYear]);

  const handlePageChange    = (p: number) => setPage(p);
  const handlePerPageChange = (n: number) => { setPerPage(n); setPage(1); };
  const handleMonthYearChange = (my: string) => {
    if (my !== selectedMonthYear) { setSelectedMonthYear(my); setPage(1); }
  };

  if (loading) {
    return (
      <DashboardLayout flash={flash}>
        <div className="flex items-center justify-center py-24 text-gray-400 gap-3">
          <svg className="animate-spin w-5 h-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-sm">Carregando registros do banco...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout flash={flash}>
        <div className="py-16 text-center">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!data || data.employees.length === 0) {
    return (
      <DashboardLayout flash={flash}>
        <div className="py-20 text-center space-y-3">
          <svg className="w-12 h-12 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-400 text-sm">Nenhum registro encontrado no banco de dados.</p>
          <Link to="/dashboard" className="text-blue-600 text-sm hover:underline">
            Fazer upload de um arquivo AFD
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout flash={flash}>
      <AfdResults
        data={data}
        onBack={() => navigate("/dashboard")}
        hideHeader
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onMonthYearChange={handleMonthYearChange}
        currentMonthYear={selectedMonthYear}
      />
    </DashboardLayout>
  );
}
